import { createElement, type ReactElement } from 'react'
import manifestJson from './imageManifest.json'

/**
 * Manifest-driven webp/responsive lookup.
 *
 * `<picture>` has no runtime fallback: once the browser selects a `<source>`
 * whose `type` it supports, a 404 on that URL renders a broken image rather
 * than falling back to the `<img src>`. Image paths on this site are
 * admin-editable (hero_config.backgroundImage and friends are DB rows) and CMS
 * uploads arrive as `/uploads/<id>.<ext>`, neither of which the build-time
 * variant script can see. So we never *derive* a webp path by string
 * substitution — we only rewrite paths that `scripts/build-images.ts` recorded
 * in `imageManifest.json`, and return null for everything else. Callers render
 * a plain `<img>` on null, which covers unknown `/images/*`, every `/uploads/*`,
 * and absolute external URLs with one rule.
 */

export interface ImageManifestEntry {
  /** Widths available as webp, ascending. */
  widths: number[]
  /** Basename without extension; variants are `<base>-<w>w.webp`. */
  base: string
}

export type ImageManifest = Record<string, ImageManifestEntry>

export interface ImageVariants {
  /** Always 'image/webp' — the one alternate format we emit. */
  type: string
  /** `srcSet` for the `<source>`, with `w` descriptors. */
  srcSet: string
  /**
   * `sizes` for the `<source>`. Required in practice: with `w` descriptors and
   * no `sizes`, the browser assumes 100vw and picks the largest variant even
   * for a 96px avatar.
   */
  sizes: string
}

/** Factory so tests can bind a fixture manifest instead of the generated one. */
export function createImageSrc(manifest: ImageManifest) {
  return function imageVariants(
    src: string | null | undefined,
    sizes = '100vw',
  ): ImageVariants | null {
    if (!src) return null
    const entry = manifest[src]
    if (!entry || entry.widths.length === 0) return null
    return {
      type: 'image/webp',
      srcSet: entry.widths
        .map((w) => `/images/${entry.base}-${w}w.webp ${w}w`)
        .join(', '),
      sizes,
    }
  }
}

/** Bound to the generated manifest. */
export const imageVariants = createImageSrc(manifestJson as ImageManifest)

/**
 * Wrap an `<img>` in `<picture>` + a webp `<source>` — but ONLY when variants
 * exist. With no variants this returns the `<img>` completely untouched, so
 * admin uploads and unknown paths render exactly the DOM they rendered before
 * this pipeline existed.
 *
 * `className="contents"` (display: contents) keeps `<picture>` out of the box
 * tree, so an `<img>`'s own layout classes — `absolute inset-0`, `aspect-square`,
 * `h-auto w-full` — resolve against the original parent exactly as before.
 *
 * Built with `createElement` rather than JSX so this can live in a `.ts` file
 * alongside the rest of the helper.
 */
export function withWebp(
  variants: ImageVariants | null,
  img: ReactElement,
): ReactElement {
  if (!variants) return img
  return createElement(
    'picture',
    { className: 'contents' },
    createElement('source', {
      type: variants.type,
      srcSet: variants.srcSet,
      sizes: variants.sizes,
    }),
    img,
  )
}
